/**
 * VprTestPanel Component
 *
 * Modal for testing VPR matching with uploaded images.
 * Same pattern as RoomRecognition/RoomTestPanel.jsx with CLIP-specific results.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  X,
  Upload,
  TestTube,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Percent,
  MapPin
} from 'lucide-react';

const VprTestPanel = ({
  guideId,
  guideName,
  testResult,
  isLoading,
  onTest,
  onClearResult,
  onClose
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleFileSelect = useCallback((file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Maximum 10MB.');
      return;
    }

    onClearResult();
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  }, [onClearResult]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleTest = useCallback(async () => {
    if (!selectedFile) return;
    await onTest(selectedFile);
  }, [selectedFile, onTest]);

  const handleClear = useCallback(() => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setSelectedFile(null);
    setPreview(null);
    onClearResult();
  }, [preview, onClearResult]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderResult = () => {
    if (!testResult) return null;

    const { match, confidence, isHighConfidence, alternatives, reason, processingTime } = testResult;

    return (
      <div className="mt-6 space-y-4">
        {/* Main Result */}
        <div className={`p-4 rounded-lg ${
          match
            ? isHighConfidence
              ? 'bg-green-50 border border-green-200'
              : 'bg-yellow-50 border border-yellow-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            {match ? (
              isHighConfidence ? (
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
              )
            ) : (
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
            )}

            <div className="flex-1">
              <h4 className={`font-medium ${
                match
                  ? isHighConfidence ? 'text-green-800' : 'text-yellow-800'
                  : 'text-red-800'
              }`}>
                {match
                  ? isHighConfidence
                    ? 'High Confidence Match!'
                    : 'Possible Match'
                  : 'No Match Found'}
              </h4>

              {match ? (
                <div className="mt-2">
                  <p className="text-lg font-semibold text-gray-900">{match.roomName}</p>
                  {match.venueName && (
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4" />
                      {match.venueName}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-red-600 mt-1">
                  {reason === 'below_threshold'
                    ? 'Closest match was below confidence threshold (0.80)'
                    : reason === 'no_rooms_in_venue'
                    ? 'No rooms registered for this venue'
                    : reason === 'ambiguous_match'
                    ? 'Ambiguous — top matches are within 0.05 margin'
                    : reason === 'no_index'
                    ? 'No CLIP index built — build the index first'
                    : 'Could not identify this room'}
                </p>
              )}
            </div>

            {/* Confidence Score */}
            {confidence != null && (
              <div className="text-right">
                <div className="flex items-center gap-1 text-2xl font-bold">
                  <Percent className="w-5 h-5 text-gray-400" />
                  <span className={
                    confidence >= 0.85
                      ? 'text-green-600'
                      : confidence >= 0.70
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }>
                    {Math.round(confidence * 100)}
                  </span>
                </div>
                <p className="text-xs text-gray-500">cosine similarity</p>
              </div>
            )}
          </div>
        </div>

        {/* Alternatives */}
        {alternatives && alternatives.length > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Other possible matches:</p>
            <div className="space-y-2">
              {alternatives.map((alt, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-800">{alt.roomName}</p>
                    {alt.venueName && (
                      <p className="text-xs text-gray-500">{alt.venueName}</p>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-600">
                    {Math.round(alt.confidence * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Processing Time */}
        {processingTime && (
          <p className="text-xs text-gray-400 text-right">
            Processed in {processingTime}ms
          </p>
        )}
      </div>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <TestTube className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Test VPR Match</h2>
              <p className="text-sm text-gray-500">{guideName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!preview ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-cyan-500 bg-cyan-50'
                  : 'border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
              />

              <Upload className={`w-12 h-12 mx-auto mb-4 ${
                dragActive ? 'text-cyan-500' : 'text-gray-400'
              }`} />

              <p className="text-gray-600 mb-2">
                Drag & drop a room photo here
              </p>
              <p className="text-sm text-gray-400 mb-4">or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200 transition-colors"
              >
                Browse Files
              </button>
            </div>
          ) : (
            <div>
              {/* Preview */}
              <div className="relative">
                <img
                  src={preview}
                  alt="Test image"
                  className="w-full h-64 object-cover rounded-lg"
                />
                <button
                  onClick={handleClear}
                  className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white rounded-lg hover:bg-opacity-70 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Test Button */}
              <button
                onClick={handleTest}
                disabled={isLoading}
                className={`w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                  isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-cyan-600 text-white hover:bg-cyan-700'
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing with CLIP...
                  </>
                ) : (
                  <>
                    <TestTube className="w-5 h-5" />
                    Run VPR Test
                  </>
                )}
              </button>

              {/* Results */}
              {renderResult()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <p className="text-xs text-gray-400 text-center">
            Uses server-side CLIP ViT-B/32. On-device uses same model via ONNX Runtime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VprTestPanel;
